import {LessonItemType, CourseItemType} from '../remote/types/responses';
import { MenuProps } from 'antd';

class LessonIndexManager {
    private static instance: LessonIndexManager;
    private lessonMap: Map<number, { htmlUrl: string; nextId: number | null }> = new Map();
    private courseId: number | null = null;

    private constructor() {}

    static getInstance(): LessonIndexManager {
        if (!LessonIndexManager.instance) {
            LessonIndexManager.instance = new LessonIndexManager();
        }
        return LessonIndexManager.instance;
    }

    initializeIndex(lessons: LessonItemType[], courseId: number) {
        // 如果是同一个课程，不需要重新初始化
        if (this.courseId === courseId && this.lessonMap.size > 0) {
            return;
        }
        
        this.lessonMap.clear();
        this.courseId = courseId;
        
        const flatLessons: LessonItemType[] = [];
        
        // 扁平化处理课程列表
        const flattenLessons = (items: LessonItemType[]) => {
            items.forEach(item => {
                if (item.lessonId) {
                    flatLessons.push(item);
                }
                if (item.children?.length) {
                    flattenLessons(item.children);
                }
            });
        };

        flattenLessons(lessons);
        
        // 按照lessonId排序
        flatLessons.sort((a, b) => (a.lessonId || 0) - (b.lessonId || 0));
        
        // 建立索引
        flatLessons.forEach((lesson, index) => {
            if (lesson.lessonId) {
                this.lessonMap.set(lesson.lessonId, {
                    htmlUrl: lesson.htmlUrl || '',
                    nextId: index < flatLessons.length - 1 ? flatLessons[index + 1].lessonId || null : null
                });
            }
        });
    }

    getNextLessonId(currentId: number): number {
        const lessonInfo = this.lessonMap.get(currentId);
        if (!lessonInfo) {
            throw new Error(`未找到ID为${currentId}的课程`);
        }
        return lessonInfo.nextId || currentId;
    }

    getHtmlUrl(lessonId: number): string {
        const lessonInfo = this.lessonMap.get(lessonId);
        if (!lessonInfo || !lessonInfo.htmlUrl) {
            throw new Error(`未找到ID为${lessonId}的课程或HTML内容不存在`);
        }
        return lessonInfo.htmlUrl;
    }

    clear() {
        this.lessonMap.clear();
        this.courseId = null;
    }
}

// 初始化课程数据的函数
export const initializeLessonIndex = (lessons: LessonItemType[], courseId: number) => {
    const manager = LessonIndexManager.getInstance();
    manager.initializeIndex(lessons, courseId);
};

// 清除课程数据的函数
export const clearLessonIndex = () => {
    const manager = LessonIndexManager.getInstance();
    manager.clear();
};

export const getNextLessonId = (currentId: number): number => {
    const manager = LessonIndexManager.getInstance();
    return manager.getNextLessonId(currentId);
};

export const getHtmlUrlByLessonId = (lessonId: number): string => {
    const manager = LessonIndexManager.getInstance();
    return manager.getHtmlUrl(lessonId);
};

export const convertCoursesToMenuItems = (courses: CourseItemType[]): MenuProps['items'] => {
    return courses.map((course) => {
        const key = course.courseId ? course.courseId.toString() : course.label;
        return {
            label: course.label,
            key: key,
            children: course.children ? convertCoursesToMenuItems(course.children) : undefined,
        };
    });
};

export const convertLessonsToMenuItems = (lessons: LessonItemType[]): MenuProps['items'] => {
    return lessons.map((lesson) => {
        const key = lesson.lessonId ? lesson.lessonId.toString() : lesson.label;
        return {
            label: lesson.label,
            key: key,
            children: lesson.children ? convertLessonsToMenuItems(lesson.children) : undefined,
        };
    });
};

export const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
  
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

